import React, { useEffect, useState } from 'react';
import { Button, Confirmation, Input, useToasts } from 'freemium-ui';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { CustomDomainDetailsType } from '../../../../../shared/types';
import Icon from '../../../../../shared/components/Icon';
import Spacer from '../../../../../shared/components/Spacer';
import { useGetAccountInfo } from '../../../../../shared/hooks';
import {
  FormContainer,
  FormContent,
  FormHeader,
  FormSection,
  FreshLink,
} from '../../../../../shared/styles';
import CopyInputBox from './components/copyInputBox';
import GenerateCname from './components/generateCname';
import {
  useActivateCustomDomain,
  useRequestCertificateCustomDomain,
  useRequestCertificateStatusCustomDomain,
  useVerifyCustomDomain,
} from '../../hooks';
import { VerifyAdornment } from '../../styles';
import { Message } from '../accountinfo/styles';
import { CustomDomainForm, Step } from './styles';

const CustomDomainMapping = () => {
  const { t } = useTranslation();
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);

  const [, setToast] = useToasts();
  const { data: accountInfo } = useGetAccountInfo({});
  const { mutate: verifyCNAME, isLoading: isLoadingVerifyCustomDomain } =
    useVerifyCustomDomain({
      setToast,
      callback: (isSuccess: boolean, response: CustomDomainDetailsType) => {
        if (isSuccess && !response?.error) {
          setCnameStates(response?.status);
        }
      },
    });
  const {
    mutate: requestCertificateCustomDomain,
    // isLoading: isLoadingRequestCertificateCustomDomain,
  } = useRequestCertificateCustomDomain({
    setToast,
    callback: (isSuccess: boolean, response: CustomDomainDetailsType) => {
      if (isSuccess && !response?.error) {
        setCnameStates(response?.status);
      }
    },
  });
  const {
    mutate: requestCertificateStatusCustomDomain,
    // isLoading: isLoadingRequestCertificateStatusCustomDomain,
  } = useRequestCertificateStatusCustomDomain({
    setToast,
    callback: (isSuccess: boolean, response: CustomDomainDetailsType) => {
      if (isSuccess && !response?.error) {
        setCnameStates(response?.status);
      }
    },
  });
  const {
    mutate: activateCustomDomain,
    isLoading: isLoadingActivateCustomDomain,
  } = useActivateCustomDomain({
    setToast,
    callback: (isSuccess: boolean, response: CustomDomainDetailsType) => {
      if (isSuccess && !response?.error) {
        setCnameStates(response?.status);
        const hostname = `https://${accountInfo?.custom_domain}`;
        localStorage.clear();
        window.location.href = hostname;
      }
    },
  });
  const [isCnameGenerated, setIsCnameGenerated] = useState<boolean>(false);
  const [isCnameVerified, setIsCnameVerified] = useState<boolean>(false);
  const [isSSLIsued, setIsSSLIsued] = useState<boolean>(false);
  const [isCnameActive, setIsCnameActive] = useState<boolean>(false);

  const handleCnameRemove = () => {
    setIsCnameGenerated(false);
    setIsCnameVerified(false);
    setIsSSLIsued(false);
  };

  const setCnameStates = (custom_domain_status) => {
    switch (custom_domain_status) {
      case 'INITIATED':
        setIsCnameGenerated(true);
        break;
      case 'DOMAIN_VERIFIED':
        setIsCnameGenerated(true);
        setIsCnameVerified(true);
        requestCertificateCustomDomain();
        break;
      case 'CERT_REQUESTED':
      case 'CERT_PROVISIONED':
        setIsCnameGenerated(true);
        setIsCnameVerified(true);
        requestCertificateStatusCustomDomain();
        break;
      case 'SSL_VERIFIED':
        setIsCnameGenerated(true);
        setIsCnameVerified(true);
        setIsSSLIsued(true);
        break;
      case 'ACTIVATED':
        setIsCnameGenerated(true);
        setIsCnameVerified(true);
        setIsSSLIsued(true);
        setIsCnameActive(true);
        break;
    }
  };

  useEffect(() => {
    setCnameStates(accountInfo?.custom_domain_status);
  }, [accountInfo]);

  return (
    <FormContainer>
      <FormHeader hideBorder>
        <div className="left-pane">
          <div>
            <div className="heading">{t('settings.custom_domain.heading')}</div>
            <div className="sub-heading">
              {`${t('settings.custom_domain.sub_heading')} `}
              <FreshLink
                href="https://support.freshsurvey.io/en/support/solutions/articles/50000004071-how-do-i-set-up-custom-domain-mapping-"
                target="_blank"
                rel="noreferrer"
              >
                {`${t('settings.custom_domain.link_text')}`}
              </FreshLink>
            </div>
          </div>
        </div>
      </FormHeader>
      <FormContent>
        <FormSection>
          <CustomDomainForm>
            <Input
              id="url-input-box"
              label={`${t('forms.labels.Your_freshsurvey_URL')}`}
              defaultValue={`https://${accountInfo?.domain}`}
              disabled
              endAdornment={
                <NavLink to="/settings/account">{t('change')}</NavLink>
              }
            />
            <Spacer y={2} />
            <Step completed={isCnameGenerated}>
              {isCnameGenerated ? (
                <div className="icon">
                  <Icon fill="none" glyph="tick" height={6} width={7} />
                </div>
              ) : null}
              <div className="title"> {t('settings.custom_domain.title')}</div>
              <div className="content">
                <GenerateCname
                  setCnameStates={setCnameStates}
                  isCnameGenerated={isCnameGenerated}
                  isCnameVerified={isCnameVerified}
                  accountInfo={accountInfo}
                  handleCnameRemove={handleCnameRemove}
                />
              </div>
              <Spacer y={1} />
            </Step>
            <Step completed={isCnameVerified} disabled={!isCnameGenerated}>
              {isCnameVerified ? (
                <div className="icon">
                  <Icon fill="none" glyph="tick" height={6} width={7} />
                </div>
              ) : null}
              <div className="title">{t('settings.CNAME_DNS_record')}</div>
              <div className="helptext">{t('settings.help_text')}</div>
              <div className="content" style={{ paddingTop: 0 }}>
                {isCnameGenerated ? (
                  <>
                    <Spacer y={0.5} />
                    <div className="flex">
                      <CopyInputBox
                        label={`${t('forms.labels.CNAME_host')}`}
                        value={
                          accountInfo?.custom_domain
                            ? accountInfo?.custom_domain
                            : ''
                        }
                      />
                      <Spacer x={1} />
                      <CopyInputBox
                        label={`${t('forms.labels.CNAME_data')}`}
                        value={
                          accountInfo?.cname_domain
                            ? accountInfo?.cname_domain
                            : ''
                        }
                      />
                    </div>
                    <Spacer y={1} />
                    {!isCnameVerified ? (
                      <Button
                        inline
                        onClick={(event: React.MouseEvent) => {
                          event.preventDefault();
                          verifyCNAME();
                        }}
                        disabled={isLoadingVerifyCustomDomain}
                      >
                        {t('settings.Verify_CNAME')}
                      </Button>
                    ) : null}
                  </>
                ) : null}
                <Spacer />
              </div>
            </Step>
            <Step completed={isSSLIsued} disabled={!isCnameVerified}>
              {isSSLIsued ? (
                <div className="icon">
                  <Icon fill="none" glyph="tick" height={6} width={7} />
                </div>
              ) : null}
              <div className="title">{t('settings.ssl_certificate_txt')}</div>
              {isCnameVerified && !isSSLIsued ? (
                <div className="content" style={{ paddingTop: 0 }}>
                  <span className="highlight">{t('settings.ETA_24hrs')}</span>
                  <Spacer y={1} />
                </div>
              ) : null}
              {isCnameVerified && isSSLIsued ? (
                <div className="inline-success">
                  <VerifyAdornment>
                    {t('settings.issued_and_verified')}
                  </VerifyAdornment>
                </div>
              ) : null}
              <Spacer y={1} />
            </Step>
            <Step
              completed={isCnameActive}
              disabled={!isSSLIsued}
              isEnd={isCnameActive}
            >
              {isCnameActive ? (
                <div className="icon">
                  <Icon fill="none" glyph="tick" height={6} width={7} />
                </div>
              ) : null}
              <div className="title">
                {t('settings.confirm_custom_domain_changes')}
              </div>
              {isCnameActive ? (
                <div className="inline-success">
                  <VerifyAdornment>{t('confirmed')}</VerifyAdornment>
                </div>
              ) : (
                <>
                  <Spacer y={1} />
                  <Button
                    id="confirm-custom-domain-btn"
                    className="submit"
                    inline
                    onClick={(event: React.MouseEvent) => {
                      event.preventDefault();
                      setIsConfirmOpen(true);
                    }}
                    disabled={isLoadingActivateCustomDomain || !isSSLIsued}
                  >
                    {t('settings.confirm_custom_domain')}
                  </Button>
                </>
              )}
            </Step>
          </CustomDomainForm>
        </FormSection>
        <Confirmation
          width="540px"
          isOpen={isConfirmOpen}
          onDismiss={() => setIsConfirmOpen(false)}
          title={`${t('settings.custom_domain_change_confirmation')}`}
          cancelButtonText={`${t('cancel')}`}
          confirmButtonText={`${t('confirm')}`}
          onConfirm={() => activateCustomDomain()}
        >
          <Message>
            {t(
              'settings.custom_domain_confirmation_message.custom_domain_modified',
            )}
            <br />
            {t(
              'settings.custom_domain_confirmation_message.confirm_changes_to_proceed',
            )}
          </Message>
        </Confirmation>
      </FormContent>
    </FormContainer>
  );
};

export default CustomDomainMapping;
